import * as Logger from './logger.js'

//
// helper (safe) for checking if object key exists
//
export function key_exists(obj, key) {
    if (obj) {
        return Object.prototype.hasOwnProperty.call(obj, key);
    }
    return false;
}

//
// canonical HTML-escaping helpers (XSS hardening). All other modules that
// need to escape untrusted data for text/attribute-context innerHTML should
// import these rather than keeping their own copy - see
// docs/XSS_HARDENING_PLAN.md (Tier C: reuse analysis).
//
export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function escapeHtmlAttribute(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
}

//
// setSafeHtml(el, html)
//
// Boundary sanitizer for the few sites that must assign attacker-influenced,
// intentionally-HTML content into innerHTML (ie: Utils.evaluate() template
// output, which may contain markup by design and can't simply be escaped).
// Strips scriptable elements/attributes rather than encoding text, so it must
// only be used at the innerHTML assignment boundary - never as a substitute
// for escapeHtml()/escapeHtmlAttribute() when building attribute values or
// text nodes from data. See docs/XSS_HARDENING_PLAN.md (Tier A).
//
const SAFE_HTML_BLOCKED_TAGS = [ 'script', 'iframe', 'object', 'embed', 'link', 'meta', 'style' ];
const SAFE_HTML_URL_ATTRS = [ 'href', 'src', 'xlink:href', 'action', 'formaction' ];

export function sanitizeHtml(html) {
    if (typeof html !== 'string' || html.length === 0) {
        return '';
    }
    const template = document.createElement('template');
    template.innerHTML = html;

    // Remove active/scriptable elements entirely.
    SAFE_HTML_BLOCKED_TAGS.forEach(tag => {
        template.content.querySelectorAll(tag).forEach(node => node.remove());
    });

    // Strip potentially dangerous attributes and javascript: URLs.
    template.content.querySelectorAll('*').forEach(node => {
        for (const attr of Array.from(node.attributes)) {
            const name = attr.name.toLowerCase();
            const value = (attr.value || '').trim().toLowerCase();
            if (name.startsWith('on') || name === 'srcdoc') {
                node.removeAttribute(attr.name);
                continue;
            }
            if (SAFE_HTML_URL_ATTRS.includes(name)
                && (value.startsWith('javascript:') || value.startsWith('data:text/html'))) {
                node.removeAttribute(attr.name);
            }
        }
    });

    return template.innerHTML;
}

export function setSafeHtml(el, html) {
    el.innerHTML = sanitizeHtml(html);
}

//
// evaluate(config)
// evaluate(config, this_context)
// evaluate(config, arg_names, args)
// evaluate(config, arg_names, args, this_context)
//
export function evaluate(config, arg_names = [], args = [], this_context = null) {
    // handle the shorthand calling convention when no args are passed: "evaluate(config, this_context)"
    if (this_context == null && Array.isArray(args) && args.length == 0 && !Array.isArray(arg_names)) {
        this_context = arg_names;
        arg_names = [];
    }
    const eval_template = (template) => {
        if (typeof(template) === 'string' && template.includes('${')) {
            try {
                const func = new Function(arg_names, "return `" + template + "`;");
                const result = func.apply(this_context, args);
                const newlineIndex = template.indexOf('\n');
                if (newlineIndex !== -1) {
                    Logger.verbose(`evaluated '${template.slice(0,newlineIndex)} ...' to '${result.slice(0,newlineIndex)} ...'`);
                } else {
                    Logger.verbose(`evaluated '${template}' to '${result}'`);
                }
                if (result.startsWith('[') || result.startsWith('{')) {
                    // template literals will always result as stringified, we need to parse arrays and objects back to proper format
                    return JSON.parse(result);
                }
                return result;
            } catch(err) {
                Logger.warn("failed to evaluate template: ", { err, template, arg_names, args });
            }
        }
        return template;
    }
    const recurse = (ref) => {
        if (Array.isArray(ref)) {
            return ref.map(x => recurse(x));
        } else if (typeof(ref) === 'object') {
            return Object.fromEntries(Object.entries(ref).map(([ key, val ]) => [ key, eval_template(val) ]));
        } else {
            return eval_template(ref);
        }
    }
    return recurse(config);
}

export async function waitFor(condition, interval_ms) {
    if (condition()) { return true; }
    await new Promise(resolve => {
        const interval = setInterval(() => {
            if (condition()) {
                clearInterval(interval);
                resolve(true);
            }
        }, interval_ms);
    });
}

export function filter(f, args, show_debug=false, internal_only=false) {
    const debug_getter = (t, args) => { if (typeof(t) === "function") { const retval = t.apply(null, args); Logger.debug(`translated '${t}' into '${retval}'`); return retval; } else { return t; } }
    const debug_array_getter = (t, args) => { if (typeof(t) === "function") { const retval = JSON.parse(t.apply(null, args)); Logger.debug(`translated '${t}' into '${retval}'`); return retval; } else if (typeof(t) === 'string') { const retval = JSON.parse(t); Logger.debug(`translated '${t}' into '${retval}'`); return retval; } else { return t; } }
    const getter = show_debug ? debug_getter : (t, args) => typeof(t) === "function" ? t.apply(null, args) : t;
    const array_getter = show_debug ? debug_array_getter : (t, args) => typeof(t) === "function" ? JSON.parse(t.apply(null, args)) : (typeof(t) === 'string' ? JSON.parse(t) : t);
    if (show_debug) {
        let result = filter(f, args, false, true);
        Logger.verbose(`filtering '${f}' result: ${result}`);
        return result;
    }
    if (!Array.isArray(f) || f.length < 2) { Logger.error("invalid filter: ", f, args); return false; }
    if (f[1] == "or") { return filter(f[0], args, internal_only, false) || ((f.length > 3) ? filter(f.slice(2), args, internal_only, false) : filter(f[2], args, internal_only, false)); }
    if (f[1] == "and") { return filter(f[0], args, internal_only, false) && ((f.length > 3) ? filter(f.slice(2), args, internal_only, false) : filter(f[2], args, internal_only, false)); }
    if (f[0] == '!!') { return !! getter(f[1], args); }
    if (f[0] == '!') { return (Array.isArray(f[1])) ? (! filter(f[1], args, internal_only, false)) : ! getter(f[1], args); }
    if (f[1] == '==') { return getter(f[0], args) == getter(f[2], args); }
    if (f[1] == '!=') { return getter(f[0], args) != getter(f[2], args); }
    if (f[1] == '===') { return getter(f[0], args) === getter(f[2], args); }
    if (f[1] == '!==') { return getter(f[0], args) !== getter(f[2], args); }
    if (f[1] == '>') { return getter(f[0], args) > getter(f[2], args); }
    if (f[1] == '>=') { return getter(f[0], args) >= getter(f[2], args); }
    if (f[1] == '<=') { return getter(f[0], args) <= getter(f[2], args); }
    if (f[1] == '<') { return getter(f[0], args) < getter(f[2], args); }
    if (f[1] == 'in') { return array_getter(f[2], args).includes(getter(f[0], args)); }
    if (f[1] == 'not in') { return ! array_getter(f[2], args).includes(getter(f[0], args)); }
    if (f[1] == 'contains') { return getter(f[0], args).includes(getter(f[2], args)); }
    if (f[1] == 'not contains') { return ! getter(f[0], args).includes(getter(f[2], args)); }
    if (f[1] == 'matches') { const regex = new RegExp(getter(f[2], args)); return regex.test(getter(f[0], args)); }
    if (f[1] == 'not matches') { const regex = new RegExp(getter(f[2], args)); return ! regex.test(getter(f[0], args)); }
    Logger.error("filter not found: ", JSON.stringify(f));
    return false;
}

export function compileFilters(rules, args) {
    Logger.debug("compiling rules: ", rules, args);
    const compiled = rules.map(rule => {
        if (typeof(rule) == "string" && rule.includes('${')) {
            return new Function(args, "return `" + rule + "`;");
        } else if (Array.isArray(rule)) {
            return compileFilters(rule, args);
        }
        return rule;
    });
    return compiled;
}

export function jsonDiff(json1, json2) {
    const str1 = JSON.stringify(json1, null, 2);
    const str2 = JSON.stringify(json2, null, 2);

    const lines1 = str1.split('\n');
    const lines2 = str2.split('\n');

    const diffResult = [];
    let i = 0, j = 0;

    while (i < lines1.length || j < lines2.length) {
        if (i < lines1.length && j < lines2.length) {
            if (lines1[i] === lines2[j]) {
                diffResult.push(`  ${lines1[i]}`); // Unchanged
            } else {
                diffResult.push(`- ${lines1[i]}`); // Deleted
                diffResult.push(`+ ${lines2[j]}`); // Added
            }
        } else if (i < lines1.length) {
            diffResult.push(`- ${lines1[i]}`); // Deleted
        } else if (j < lines2.length) {
            diffResult.push(`+ ${lines2[j]}`); // Added
        }
        i++;
        j++;
    }
    return diffResult.join('\n');
}

//
// create a clone of an object with only specified properties copied
// usage: pick(myObj, field1, field2);
//
export function pick(obj, ...props) {
    return Object.assign({}, ...props.map(prop => ({[prop]: obj[prop]})));
}
// same as above, except fields passed as array instead of varargs
// usage: pick_fields(myObj, [ field1, field2 ]);
export function pick_fields(obj, fields) {
    return Object.assign({}, ...fields.map(prop => ({[prop]: obj[prop]})));
}
// same as above, but excludes fields with undefined values
export function pickIfDefined(obj, ...props) {
    return Object.fromEntries(Object.entries(Object.assign({}, ...props.map(prop => ({[prop]: obj[prop]})))).filter(([key,value]) => value !== undefined));
}

// Utility: Debounce function
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export function getStartOfDay() {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
}
export function getEndOfDay() {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return now;
}
export function getStartOfWeek() {
    const now = new Date();
    const day = now.getDay(); // 0 (Sun) to 6 (Sat)
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    const startOfWeek = new Date(now.setDate(diff));
    startOfWeek.setHours(0, 0, 0, 0);
    return startOfWeek;
}
export function getEndOfWeek() {
    const startOfWeek = getStartOfWeek();
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    return endOfWeek;
}
export function getStartOfMonth() {
    const now = new Date();
    now.setDate(1);
    now.setHours(0, 0, 0, 0);
    return now;
}
export function getEndOfMonth() {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);
    return endOfMonth;
}
export function getStartOfYear() {
    const now = new Date();
    now.setMonth(0, 1); // January 1st
    now.setHours(0, 0, 0, 0);
    return now;
}
export function getEndOfYear() {
    const now = new Date();
    now.setMonth(11, 31); // December 31st
    now.setHours(23, 59, 59, 999);
    return now;
}

export function getCurrentUTCMonth() {
    return Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1);
}
export function getCurrentUTCQuarter() {
    const now = new Date();
    const quarterStartMonth = Math.floor(now.getUTCMonth() / 3) * 3;
    return Date.UTC(now.getUTCFullYear(), quarterStartMonth, 1);
}
export function getCurrentUTCDay() {
    return new Date(new Date().setUTCDate(new Date().getUTCDate())).setUTCHours(0,0,0,0);
}
export function getCurrentUTCHour() {
    return new Date(new Date().setUTCDate(new Date().getUTCDate())).setUTCMinutes(0,0,0);
}

export function getLocaleWeekStart(locale = null) {
    try {
        const resolvedLocale = locale || navigator?.language || 'en-US';
        const firstDay = new Intl.Locale(resolvedLocale).weekInfo?.firstDay;
        if (Number.isInteger(firstDay)) {
            return firstDay % 7;
        }
    } catch (err) {
        Logger.debug('Falling back to Sunday week start', err?.message || err);
    }
    return 0;
}
export function last30DaysUTC() {
    return new Date(new Date().setUTCDate(new Date().getUTCDate() - 30)).setUTCHours(0,0,0,0);
}
export function last7DaysUTC() {
    return new Date(new Date().setUTCDate(new Date().getUTCDate() - 7)).setUTCHours(0,0,0,0);
}
export function last24HoursUTC() {
    return new Date(new Date().setUTCHours(new Date().getUTCHours() - 24)).setUTCMinutes(0,0,0);
}

// Snapshot helper functions for stats view
export function getEndOfLastCompleteWeek(userLocaleWeekStart = 0) {
    // userLocaleWeekStart: 0 = Sunday (US), 1 = Monday (EU), etc.
    const now = new Date();
    const currentDayOfWeek = now.getUTCDay();
    
    // Calculate days to subtract to get to the last complete week's end
    // If we're in the middle of a week, go back to the end of the previous week
    const daysToLastWeekEnd = (currentDayOfWeek - userLocaleWeekStart + 7) % 7;
    const daysToSubtract = daysToLastWeekEnd === 0 ? 7 : daysToLastWeekEnd;
    
    const lastWeekEnd = new Date(now);
    lastWeekEnd.setUTCDate(lastWeekEnd.getUTCDate() - daysToSubtract);
    lastWeekEnd.setUTCHours(23, 59, 59, 999);
    
    return lastWeekEnd.getTime();
}

export function getEndOfLastMonth() {
    const now = new Date();
    const lastMonthEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59, 999));
    return lastMonthEnd.getTime();
}

export function getEndOfLastQuarter() {
    const now = new Date();
    const currentQuarter = Math.floor(now.getUTCMonth() / 3);
    const lastQuarterEndMonth = currentQuarter === 0 ? 11 : (currentQuarter - 1) * 3 + 2;
    const lastQuarterEndYear = currentQuarter === 0 ? now.getUTCFullYear() - 1 : now.getUTCFullYear();
    const lastDayOfMonth = new Date(Date.UTC(lastQuarterEndYear, lastQuarterEndMonth + 1, 0, 23, 59, 59, 999));
    return lastDayOfMonth.getTime();
}

export function generateSnapshotOptions(analysisWindow, userLocaleWeekStart = 0) {
    const now = new Date();
    const options = [];

    const formatUtcDateLabel = (timestamp) => new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(timestamp));

    const formatUtcMonthLabel = (timestamp) => new Intl.DateTimeFormat(undefined, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(timestamp));

    const getCurrentWeekStart = () => {
        const localNow = new Date();
        const currentDay = localNow.getDay();
        const diff = (currentDay - userLocaleWeekStart + 7) % 7;
        localNow.setDate(localNow.getDate() - diff);
        return Date.UTC(localNow.getFullYear(), localNow.getMonth(), localNow.getDate(), 0, 0, 0, 0);
    };

    switch (analysisWindow) {
        case 'day': {
            for (let offset = 0; offset < 30; offset++) {
                const anchor = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - offset, 0, 0, 0, 0);
                let label = formatUtcDateLabel(anchor);
                if (offset === 0) {
                    label = 'Today';
                } else if (offset === 1) {
                    label = 'Yesterday';
                }
                options.push({ label, timestamp: anchor });
            }
            break;
        }

        case 'week': {
            const currentWeekStart = getCurrentWeekStart();
            for (let offset = 0; offset < 12; offset++) {
                const anchorDate = new Date(currentWeekStart);
                anchorDate.setUTCDate(anchorDate.getUTCDate() - (offset * 7));
                const anchor = anchorDate.getTime();
                options.push({
                    label: `Week of ${formatUtcDateLabel(anchor)}`,
                    timestamp: anchor,
                });
            }
            break;
        }

        case 'month': {
            for (let offset = 0; offset < 12; offset++) {
                const anchor = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1, 0, 0, 0, 0);
                options.push({ label: formatUtcMonthLabel(anchor), timestamp: anchor });
            }
            break;
        }

        case 'quarter': {
            const currentQuarterStartMonth = Math.floor(now.getUTCMonth() / 3) * 3;
            for (let offset = 0; offset < 12; offset++) {
                const anchor = Date.UTC(now.getUTCFullYear(), currentQuarterStartMonth - (offset * 3), 1, 0, 0, 0, 0);
                const anchorDate = new Date(anchor);
                const quarter = Math.floor(anchorDate.getUTCMonth() / 3) + 1;
                options.push({
                    label: `Q${quarter} ${anchorDate.getUTCFullYear()}`,
                    timestamp: anchor,
                });
            }
            break;
        }

        case 'all':
        default:
            break;
    }

    return options;
}

// convert a JSON array of objects into CSV data format
export function convertJsonToCSV(json) {
    return json.map(row => row
            .map(String)  // convert every value to String
            .map(v => {
                const escaped = v.replaceAll('"', '""'); // escape double quotes
                // Quote any field that contains CSV special chars (comma, quote, newline)
                return /[",\r\n]/.test(v) ? `"${escaped}"` : escaped;
            })
            .join(',')  // comma-separated
        ).join('\r\n');  // rows starting on new lines
}


